/**
 * Article Block Queries
 *
 * Server-side queries for fetching article block data.
 * Supports fetching articles, podcasts, and podcast episodes by their IDs.
 * Used by ArticleBlockRenderer to fetch its own data with pagination.
 */

import { query } from "@solidjs/router";
import type { CMSArticle, CMSPodcast, CMSPodcastEpisode } from "~/types/cms";
import { logger } from "~/lib/logger/logger";
import { env } from "~/config/env";
import { buildCollectionByIdsUrl } from "../queryUrls";
import { fetchFromCMS, reorderByIds } from "../queryUtils";

interface ArticlesResponse {
  docs: CMSArticle[];
  totalDocs: number;
}

interface PodcastsResponse {
  docs: CMSPodcast[];
  totalDocs: number;
}

interface PodcastEpisodesResponse {
  docs: CMSPodcastEpisode[];
  totalDocs: number;
}

/**
 * Batched fetch result containing all content types
 */
export interface ArticleBlockBatchResult {
  articles: CMSArticle[];
  podcasts: CMSPodcast[];
  podcastEpisodes: CMSPodcastEpisode[];
}

/**
 * Item reference with just the ID and type (for unpopulated data)
 */
export interface ArticleBlockItemRef {
  type: "article" | "podcastEpisode" | "podcast";
  id: string;
}

/**
 * Fetch articles by their IDs.
 * depth=2: content → heroBlock → image (fully populated media)
 *
 * @param ids - Array of article IDs to fetch
 * @returns Array of articles with card-relevant fields, preserving input order
 */
export const getArticlesByIdsQuery = query(async (ids: string[]) => {
  "use server";

  const LOG_PREFIX = "[articleBlockArticles]";

  logger.info(`${LOG_PREFIX} Query called with IDs: ${JSON.stringify(ids)}`);

  if (!ids || ids.length === 0) {
    logger.info(`${LOG_PREFIX} No IDs provided, returning empty array`);
    return [];
  }

  const url = `${env.cmsApiUrl}${buildCollectionByIdsUrl({ collection: "articles", ids, depth: 2, filterPublished: true, tenantSlug: env.tenantSlug })}`;

  logger.info(`${LOG_PREFIX} Fetching from URL: ${url}`);

  const data = await fetchFromCMS<ArticlesResponse>(url, { logPrefix: LOG_PREFIX });

  logger.info(
    `${LOG_PREFIX} CMS Response: ${data?.docs?.length ?? 0} docs, totalDocs: ${data?.totalDocs ?? "undefined"}`
  );

  if (!data?.docs) {
    logger.warn(`${LOG_PREFIX} No articles found for IDs: ${ids.join(", ")}`);
    return [];
  }

  const orderedDocs = reorderByIds(data.docs, ids);
  logger.info(
    `${LOG_PREFIX} Fetched ${orderedDocs.length} articles, IDs: ${orderedDocs.map((d) => d.id).join(", ")}`
  );
  return orderedDocs;
}, "articleBlockArticles");

/**
 * Fetch podcasts by their IDs.
 * depth=2: content → heroBlock → image (fully populated media)
 *
 * @param ids - Array of podcast IDs to fetch
 * @returns Array of podcasts with card-relevant fields, preserving input order
 */
export const getPodcastsByIdsQuery = query(async (ids: string[]) => {
  "use server";

  const LOG_PREFIX = "[articleBlockPodcasts]";

  if (!ids || ids.length === 0) {
    return [];
  }

  const url = `${env.cmsApiUrl}${buildCollectionByIdsUrl({ collection: "podcasts", ids, depth: 2, filterPublished: true, tenantSlug: env.tenantSlug })}`;

  const data = await fetchFromCMS<PodcastsResponse>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs) {
    logger.warn(`${LOG_PREFIX} No podcasts found for IDs: ${ids.join(", ")}`);
    return [];
  }

  const orderedDocs = reorderByIds(data.docs, ids);
  logger.info(`${LOG_PREFIX} Fetched ${orderedDocs.length} podcasts`);
  return orderedDocs;
}, "articleBlockPodcasts");

/**
 * Fetch podcast episodes by their IDs.
 * depth=3: content → heroBlock → image, podcast → slug (for URL building)
 *
 * @param ids - Array of podcast episode IDs to fetch
 * @returns Array of podcast episodes with card-relevant fields, preserving input order
 */
export const getPodcastEpisodesByIdsQuery = query(async (ids: string[]) => {
  "use server";

  const LOG_PREFIX = "[articleBlockEpisodes]";

  if (!ids || ids.length === 0) {
    return [];
  }

  const url = `${env.cmsApiUrl}${buildCollectionByIdsUrl({ collection: "podcast-episodes", ids, depth: 3, filterPublished: true, tenantSlug: env.tenantSlug })}`;

  const data = await fetchFromCMS<PodcastEpisodesResponse>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs) {
    logger.warn(`${LOG_PREFIX} No podcast episodes found for IDs: ${ids.join(", ")}`);
    return [];
  }

  const orderedDocs = reorderByIds(data.docs, ids);
  logger.info(`${LOG_PREFIX} Fetched ${orderedDocs.length} podcast episodes`);
  return orderedDocs;
}, "articleBlockEpisodes");

/**
 * Batch fetch all content types needed for an article block.
 * Groups IDs by type and fetches them in parallel.
 *
 * @param items - Array of item references with type and ID
 * @returns Object containing fetched articles, podcasts, and episodes
 */
export const getArticleBlockContentQuery = query(async (items: ArticleBlockItemRef[]) => {
  "use server";

  const LOG_PREFIX = "[articleBlockContent]";

  logger.info(
    `${LOG_PREFIX} Query called with ${items?.length ?? 0} items: ${JSON.stringify(items)}`
  );

  if (!items || items.length === 0) {
    logger.info(`${LOG_PREFIX} No items provided, returning empty result`);
    return { articles: [], podcasts: [], podcastEpisodes: [] };
  }

  // Group IDs by type
  const articleIds: string[] = [];
  const podcastIds: string[] = [];
  const episodeIds: string[] = [];

  for (const item of items) {
    switch (item.type) {
      case "article":
        articleIds.push(item.id);
        break;
      case "podcast":
        podcastIds.push(item.id);
        break;
      case "podcastEpisode":
        episodeIds.push(item.id);
        break;
    }
  }

  logger.info(
    `${LOG_PREFIX} Fetching: ${articleIds.length} articles (${articleIds.join(",")}), ${podcastIds.length} podcasts, ${episodeIds.length} episodes`
  );

  // Fetch all types in parallel
  const [articles, podcasts, podcastEpisodes] = await Promise.all([
    articleIds.length > 0 ? getArticlesByIdsQuery(articleIds) : Promise.resolve([]),
    podcastIds.length > 0 ? getPodcastsByIdsQuery(podcastIds) : Promise.resolve([]),
    episodeIds.length > 0 ? getPodcastEpisodesByIdsQuery(episodeIds) : Promise.resolve([]),
  ]);

  logger.info(
    `${LOG_PREFIX} Fetch results: ${articles?.length ?? 0} articles, ${podcasts?.length ?? 0} podcasts, ${podcastEpisodes?.length ?? 0} episodes`
  );

  return {
    articles: articles || [],
    podcasts: podcasts || [],
    podcastEpisodes: podcastEpisodes || [],
  };
}, "articleBlockContent");

/**
 * Parameters for auto-populated article block pagination
 */
export interface AutoPopulatedParams {
  contentType: "articles" | "podcastEpisodes" | "podcasts" | "all";
  page: number;
  limit: number;
  sortBy?: "publishedAt" | "publishedAtAsc" | "title" | "titleDesc";
  filterStation?: string;
}

/**
 * Result for auto-populated query with pagination info
 */
export interface AutoPopulatedResult {
  items: Array<{
    type: "article" | "podcastEpisode" | "podcast";
    data: CMSArticle | CMSPodcast | CMSPodcastEpisode;
  }>;
  totalDocs: number;
  hasMore: boolean;
}

/**
 * Fetch auto-populated content with pagination.
 * Used for articleBlocks with populationMode: all, byStation
 *
 * @param params - Pagination and filter parameters
 * @returns Paginated content with total count
 */
export const getAutoPopulatedContentQuery = query(
  async (params: AutoPopulatedParams): Promise<AutoPopulatedResult> => {
    "use server";

    const LOG_PREFIX = "[autoPopulatedContent]";
    const { contentType, page, limit, sortBy = "publishedAt", filterStation } = params;

    logger.info(`${LOG_PREFIX} Query called`, { contentType, page, limit, sortBy });

    // Build sort parameter
    let sort: string;
    switch (sortBy) {
      case "publishedAtAsc":
        sort = "publishedAt";
        break;
      case "titleDesc":
        sort = "-title";
        break;
      case "title":
        sort = "title";
        break;
      default:
        sort = "-publishedAt";
    }

    // Build filter params
    // depth=2 for stations[].branding.primaryColor (badge colors)
    // Dot-syntax tenant filter is honored server-side; bracket-syntax is silently
    // ignored (would leak other tenants' content into auto-populated blocks).
    const tenantFilter = `&where[tenant.slug][equals]=${env.tenantSlug}`;
    const stationFilter = filterStation ? `&where[stations][in]=${filterStation}` : "";
    const baseFilters = `&where[_status][equals]=published${tenantFilter}`;

    const items: AutoPopulatedResult["items"] = [];
    let totalDocs = 0;

    // Fetch based on content type
    if (contentType === "articles" || contentType === "all") {
      const url = `${env.cmsApiUrl}/api/articles?depth=2&page=${page}&limit=${limit}&sort=${sort}${baseFilters}${stationFilter}`;
      const data = await fetchFromCMS<ArticlesResponse>(url, { logPrefix: LOG_PREFIX });
      if (data?.docs) {
        for (const doc of data.docs) {
          items.push({ type: "article", data: doc });
        }
        totalDocs = Math.max(totalDocs, data.totalDocs || 0);
      }
    }

    if (contentType === "podcasts" || contentType === "all") {
      const url = `${env.cmsApiUrl}/api/podcasts?depth=2&page=${page}&limit=${limit}&sort=${sort}${baseFilters}${stationFilter}`;
      const data = await fetchFromCMS<PodcastsResponse>(url, { logPrefix: LOG_PREFIX });
      if (data?.docs) {
        for (const doc of data.docs) {
          items.push({ type: "podcast", data: doc });
        }
        totalDocs = Math.max(totalDocs, data.totalDocs || 0);
      }
    }

    // Episodes: no stationFilter in query (episodes inherit stations from parent podcast)
    // Station filtering is done post-fetch via parent podcast's stations
    if (contentType === "podcastEpisodes" || contentType === "all") {
      const url = `${env.cmsApiUrl}/api/podcast-episodes?depth=2&page=${page}&limit=${limit}&sort=${sort}${baseFilters}`;
      const data = await fetchFromCMS<PodcastEpisodesResponse>(url, { logPrefix: LOG_PREFIX });
      if (data?.docs) {
        let episodes = data.docs;

        // Client-side station filtering for episodes (via parent podcast's stations)
        if (filterStation) {
          episodes = episodes.filter((episode) => {
            const podcast = episode.podcast;
            if (!podcast || typeof podcast !== "object") return false;
            const podcastStations = podcast.stations;
            if (!podcastStations || !Array.isArray(podcastStations)) return false;
            return podcastStations.some((station) => {
              const stationId = typeof station === "object" ? station?.id : station;
              return String(stationId) === String(filterStation);
            });
          });
        }

        for (const doc of episodes) {
          items.push({ type: "podcastEpisode", data: doc });
        }
        totalDocs = Math.max(totalDocs, data.totalDocs || 0);
      }
    }

    const hasMore = page * limit < totalDocs;
    logger.info(
      `${LOG_PREFIX} Returning ${items.length} items, totalDocs: ${totalDocs}, hasMore: ${hasMore}`
    );

    return { items, totalDocs, hasMore };
  },
  "autoPopulatedContent"
);
